import {
  healthSummary,
  LabelSelector,
  loadBalancerPrice,
  serviceText,
  targetHealth,
  type LoadBalancer,
  type LoadBalancerAlgorithm,
  type LoadBalancerService,
  type LoadBalancerTarget,
  type TargetHealthEntry,
} from "@cloudserver/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Globe,
  Lock,
  LockOpen,
  Pencil,
  Plus,
  Server,
  Tags,
  Trash2,
  Unplug,
  Waypoints,
} from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../api/client.js";
import {
  useAddTarget,
  useAttachLoadBalancerNetwork,
  useChangeAlgorithm,
  useChangeLoadBalancerType,
  useDeleteLoadBalancer,
  useDeleteService,
  useDetachLoadBalancerNetwork,
  useLoadBalancer,
  useLoadBalancerProtection,
  useLoadBalancerReverseDns,
  usePublicInterface,
  useRemoveTarget,
  useUpdateLoadBalancer,
} from "../api/load-balancers.js";
import { useNetworks } from "../api/networks.js";
import { progressOf, useRunningOperations } from "../api/operations.js";
import { useCatalogue, useProject } from "../api/queries.js";
import { useServers } from "../api/servers.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError } from "../ui/feedback.js";
import { dataSize, money } from "../ui/format.js";
import { EmptyState, PageHeader, Section, Tabs } from "../ui/layout.js";
import { Dialog } from "../ui/overlay.js";
import { Alert, Badge, Button, Card, Spinner, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { formatLabels } from "./labels.js";
import { FieldProblems, ServiceDialog } from "./load-balancer-service.js";
import { DeleteLoadBalancerDialog, HealthBadge, monthlyText } from "./load-balancers.js";
import { NameAndLabels } from "./network.js";
import { failureToast, SelectField } from "./networks.js";
import { ReverseDnsDialog } from "./public-ips.js";

export const LOAD_BALANCER_TABS = [
  "overview",
  "services",
  "targets",
  "networking",
  "type",
  "settings",
  "delete",
] as const;
export type LoadBalancerTab = (typeof LOAD_BALANCER_TABS)[number];

interface TabProps {
  projectId: string;
  lb: LoadBalancer;
  readOnly: boolean;
  busy: boolean;
}

const HEALTH_TONE = { healthy: "ok", unhealthy: "danger", unknown: "neutral" } as const;

/** One badge per service port: "443 healthy". */
function HealthPerPort({ health }: { health: TargetHealthEntry[] }) {
  const { t } = useTranslation();
  if (health.length === 0) return <span className="text-muted">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {health.map((h) => (
        <Badge key={h.listenPort} tone={HEALTH_TONE[h.status]}>
          {t("loadBalancers.healthOnPort", {
            port: h.listenPort,
            status: t(`loadBalancers.health.${h.status}`),
          })}
        </Badge>
      ))}
    </span>
  );
}

function Fact({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

function OverviewTab({ projectId, lb, readOnly, busy }: TabProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const types = useCatalogue(projectId, "load-balancer-types").data;
  const prices = useCatalogue(projectId, "prices").data;
  const algorithm = useChangeAlgorithm(projectId, lb.id);
  const reverseDns = useLoadBalancerReverseDns(projectId);
  const [dns, setDns] = useState<"ipv4" | "ipv6" | null>(null);
  const [chosen, setChosen] = useState<LoadBalancerAlgorithm>(lb.algorithm);
  const ptr = (ip: string | null) => lb.reverseDns.find((d) => d.ip === ip)?.dnsPtr ?? "—";
  const healthy = lb.targets.filter((tg) => targetHealth(tg) === "healthy").length;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <Section title={t("loadBalancers.addresses")}>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
            <Fact label="IPv4">
              <span className="font-mono">{lb.ipv4 ?? "—"}</span>
              <span className="block text-xs text-muted">{ptr(lb.ipv4)}</span>
            </Fact>
            <Fact label="IPv6">
              <span className="font-mono">{lb.ipv6 ?? "—"}</span>
              <span className="block text-xs text-muted">{ptr(lb.ipv6)}</span>
            </Fact>
            <Fact label={t("loadBalancers.publicInterface")}>
              {lb.publicInterface ? (
                <Badge tone="ok">{t("loadBalancers.on")}</Badge>
              ) : (
                <Badge>{t("loadBalancers.off")}</Badge>
              )}
            </Fact>
            <Fact label={t("loadBalancers.networks")}>
              {lb.networks.length
                ? lb.networks.map((n) => `${n.ip} (${n.name})`).join(", ")
                : t("loadBalancers.noNetwork")}
            </Fact>
          </dl>
          <div className="flex flex-wrap gap-2">
            {(["ipv4", "ipv6"] as const).map((family) => (
              <Button
                key={family}
                variant="secondary"
                disabled={readOnly || busy || !lb[family]}
                icon={<Waypoints className="size-4" aria-hidden />}
                onClick={() => setDns(family)}
              >
                {t(family === "ipv4" ? "loadBalancers.dnsV4" : "loadBalancers.dnsV6")}
              </Button>
            ))}
          </div>
        </Section>
      </Card>
      <Card>
        <Section title={t("loadBalancers.healthTitle")}>
          <div className="flex items-center gap-2 text-sm">
            <HealthBadge health={healthSummary(lb)} />
            <span className="text-muted">
              {t("loadBalancers.healthyOf", { healthy, count: lb.targets.length })}
            </span>
          </div>
          <p className="text-sm text-muted">{t("loadBalancers.healthLead")}</p>
          <Link
            to="/projects/$projectId/load-balancers/$loadBalancerId"
            params={{ projectId, loadBalancerId: lb.id }}
            search={{ tab: "targets" }}
            className="text-sm text-accent hover:underline"
          >
            {t("loadBalancers.openTargets")}
          </Link>
        </Section>
      </Card>
      <Card>
        <Section title={t("loadBalancers.algorithm")}>
          <fieldset className="flex flex-col gap-2 text-sm">
            <legend className="sr-only">{t("loadBalancers.algorithm")}</legend>
            {(["round_robin", "least_connections"] as const).map((a) => (
              <label key={a} className="flex min-h-6 items-start gap-2">
                <input
                  type="radio"
                  name="algorithm"
                  checked={chosen === a}
                  disabled={readOnly}
                  onChange={() => setChosen(a)}
                  className="mt-0.5 size-4 accent-accent"
                />
                <span className="flex flex-col">
                  <span className="font-medium">{t(`loadBalancers.algorithms.${a}`)}</span>
                  <span className="text-xs text-muted">
                    {t(`loadBalancers.algorithmHints.${a}`)}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          <div>
            <Button
              variant="secondary"
              busy={algorithm.isPending}
              disabled={readOnly || busy || chosen === lb.algorithm}
              onClick={() =>
                algorithm.mutate(
                  { algorithm: chosen },
                  {
                    onSuccess: () =>
                      toast({ tone: "ok", title: t("loadBalancers.algorithmSaving") }),
                    onError: (error) => toast(failureToast(t, error)),
                  },
                )
              }
            >
              {t("loadBalancers.algorithmSave")}
            </Button>
          </div>
        </Section>
      </Card>
      <Card>
        <Section title={t("loadBalancers.limitsTitle")}>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
            <Fact label={t("loadBalancers.type")}>{lb.type.toUpperCase()}</Fact>
            <Fact label={t("loadBalancers.services")}>
              {lb.services.length} / {lb.limits.maxServices}
            </Fact>
            <Fact label={t("loadBalancers.targets")}>
              {lb.targets.length} / {lb.limits.maxTargets}
            </Fact>
            <Fact label={t("loadBalancers.maxConnections")}>
              {lb.limits.maxConnections.toLocaleString(i18n.language)}
            </Fact>
            <Fact label={t("loadBalancers.trafficOut")}>
              {lb.traffic.outBytes === null ? "—" : dataSize(lb.traffic.outBytes, i18n.language)}
              {lb.traffic.includedBytes !== null && (
                <span className="block text-xs text-muted">
                  {t("loadBalancers.trafficIncluded", {
                    size: dataSize(lb.traffic.includedBytes, i18n.language),
                  })}
                </span>
              )}
            </Fact>
            <Fact label={t("loadBalancers.pricePerMonth")}>
              {monthlyText(lb, types, prices?.currency, i18n.language)}
            </Fact>
          </dl>
        </Section>
      </Card>
      {dns && lb[dns] && (
        <ReverseDnsDialog
          projectId={projectId}
          ip={{ id: lb.id, family: dns, address: lb[dns] ?? "", reverseDns: lb.reverseDns }}
          mutation={reverseDns}
          onClose={() => setDns(null)}
        />
      )}
    </div>
  );
}

function ServicesTab({ projectId, lb, readOnly, busy }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const remove = useDeleteService(projectId, lb.id);
  const [editing, setEditing] = useState<LoadBalancerService | "new" | null>(null);
  const [deleting, setDeleting] = useState<LoadBalancerService | null>(null);
  const full = lb.services.length >= lb.limits.maxServices;
  const why = readOnly ? t("errors.reasons.read_only_token") : busy ? t("servers.busy") : "";
  const addButton = (
    <Button
      onClick={() => setEditing("new")}
      disabled={readOnly || busy || full}
      icon={<Plus className="size-4" aria-hidden />}
    >
      {t("loadBalancers.service.add")}
    </Button>
  );
  const portHealth = (port: number) => {
    const statuses = lb.targets.flatMap((tg) =>
      (tg.type === "label_selector" ? tg.servers.flatMap((s) => s.health) : tg.health).filter(
        (h) => h.listenPort === port,
      ),
    );
    return {
      healthy: statuses.filter((h) => h.status === "healthy").length,
      count: statuses.length,
    };
  };
  return (
    <>
      <Section
        title={t("loadBalancers.services")}
        description={t("loadBalancers.servicesLead", { max: lb.limits.maxServices })}
        actions={lb.services.length ? addButton : null}
      >
        {full && <Alert tone="neutral">{t("errors.reasons.lb_service_limit")}</Alert>}
        <DataTable<LoadBalancerService>
          label={t("loadBalancers.services")}
          rows={lb.services}
          rowKey={(s) => String(s.listenPort)}
          empty={
            <EmptyState
              icon={<Plus className="size-6" aria-hidden />}
              title={t("loadBalancers.noServices")}
              action={addButton}
              help="load-balancers"
            >
              {t("loadBalancers.noServicesLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "forward",
              header: t("loadBalancers.service.forward"),
              primary: true,
              cell: (s) => (
                <span className="flex flex-col">
                  <span className="font-mono font-medium">{serviceText(s)}</span>
                  <span className="text-xs text-muted">{s.protocol.toUpperCase()}</span>
                </span>
              ),
            },
            {
              key: "check",
              header: t("loadBalancers.service.healthCheck"),
              cell: (s) => (
                <span className="text-xs">
                  {s.healthCheck.protocol.toUpperCase()} :{s.healthCheck.port}
                  {s.healthCheck.http ? ` ${s.healthCheck.http.path}` : ""}
                  <span className="block text-muted">
                    {t("loadBalancers.service.every", { seconds: s.healthCheck.intervalSeconds })}
                  </span>
                </span>
              ),
            },
            {
              key: "health",
              header: t("loadBalancers.healthTitle"),
              cell: (s) => {
                const h = portHealth(s.listenPort);
                return h.count === 0 ? (
                  <span className="text-muted">—</span>
                ) : (
                  <Badge tone={h.healthy === h.count ? "ok" : h.healthy > 0 ? "warn" : "danger"}>
                    {t("loadBalancers.healthyOf", { healthy: h.healthy, count: h.count })}
                  </Badge>
                );
              },
            },
            {
              key: "certificates",
              header: t("loadBalancers.service.certificates"),
              cell: (s) =>
                s.certificates.length ? (
                  s.certificates.map((c) => c.name).join(", ")
                ) : (
                  <span className="text-muted">—</span>
                ),
            },
            {
              key: "options",
              header: t("loadBalancers.service.options"),
              cell: (s) => (
                <span className="flex flex-wrap gap-1">
                  {s.http?.redirectHttp && (
                    <Badge>{t("loadBalancers.service.redirectShort")}</Badge>
                  )}
                  {s.http?.stickySessions && (
                    <Badge>{t("loadBalancers.service.stickyShort")}</Badge>
                  )}
                  {s.proxyProtocol && <Badge>{t("loadBalancers.service.proxyShort")}</Badge>}
                  {!s.http?.redirectHttp && !s.http?.stickySessions && !s.proxyProtocol && (
                    <span className="text-muted">—</span>
                  )}
                </span>
              ),
            },
          ]}
          rowMenu={(s) => [
            [
              {
                label: t("loadBalancers.service.edit"),
                icon: <Pencil className="size-4" aria-hidden />,
                disabled: readOnly || busy,
                reason: why,
                onSelect: () => setEditing(s),
              },
            ],
            [
              {
                label: t("loadBalancers.service.delete"),
                icon: <Trash2 className="size-4" aria-hidden />,
                danger: true,
                disabled: readOnly || busy,
                reason: why,
                onSelect: () => setDeleting(s),
              },
            ],
          ]}
        />
      </Section>
      {editing && (
        <ServiceDialog
          projectId={projectId}
          lb={lb}
          service={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("loadBalancers.service.deleteTitle", {
          service: deleting ? serviceText(deleting) : "",
        })}
        consequences={[
          t("loadBalancers.service.deleteStops", { port: deleting?.listenPort ?? "" }),
        ]}
        actionLabel={t("loadBalancers.service.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.listenPort, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("loadBalancers.service.deleting") });
              setDeleting(null);
            },
            onError: (error) => toast(failureToast(t, error)),
          })
        }
      />
    </>
  );
}

type TargetDialog = "server" | "selector" | "ip" | null;

function AddTargetDialog({
  projectId,
  lb,
  kind,
  onClose,
}: {
  projectId: string;
  lb: LoadBalancer;
  kind: Exclude<TargetDialog, null>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const servers = useServers(projectId);
  const add = useAddTarget(projectId, lb.id);
  const [serverId, setServerId] = useState("");
  const [selector, setSelector] = useState("");
  const [ip, setIp] = useState("");
  const [usePrivateIp, setUsePrivateIp] = useState(lb.networks.length > 0);
  const [tried, setTried] = useState(false);
  const lbNets = new Set(lb.networks.map((n) => n.networkRef));
  const server = servers.data?.find((s) => s.id === serverId);
  const shared = server ? server.privateIps.some((p) => lbNets.has(p.networkRef)) : lbNets.size > 0;
  const already = new Set(
    lb.targets.flatMap((tg) => (tg.type === "server" && tg.server.id ? [tg.server.id] : [])),
  );
  const selectorValid = LabelSelector.safeParse(selector).success;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    const body =
      kind === "server"
        ? serverId
          ? { type: "server" as const, serverId, usePrivateIp: usePrivateIp && shared }
          : null
        : kind === "selector"
          ? selectorValid
            ? {
                type: "label_selector" as const,
                selector: selector.trim(),
                usePrivateIp: usePrivateIp && shared,
              }
            : null
          : ip.trim()
            ? { type: "ip" as const, ip: ip.trim() }
            : null;
    if (!body) return;
    add.mutate(body, {
      onSuccess: () => {
        toast({ tone: "ok", title: t("loadBalancers.targetAdding") });
        onClose();
      },
    });
  };
  const title = {
    server: t("loadBalancers.addServer"),
    selector: t("loadBalancers.addSelector"),
    ip: t("loadBalancers.addIp"),
  }[kind];
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={title}
      description={t(`loadBalancers.addTargetLead.${kind}`, { zone: lb.zone })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="lb-target" busy={add.isPending}>
            {t("loadBalancers.addTarget")}
          </Button>
        </>
      }
    >
      <form id="lb-target" onSubmit={submit} noValidate className="flex flex-col gap-4">
        {kind === "server" &&
          (servers.isPending ? (
            <Spinner label={t("common.loading")} />
          ) : (
            <SelectField
              label={t("loadBalancers.server")}
              name="server"
              value={serverId}
              onChange={setServerId}
              hint={t("loadBalancers.serverHint")}
            >
              <option value="">{t("loadBalancers.chooseServer")}</option>
              {(servers.data ?? []).map((s) => {
                const why =
                  s.zone !== lb.zone
                    ? t("loadBalancers.why.otherZone")
                    : already.has(s.id)
                      ? t("loadBalancers.why.alreadyTarget")
                      : null;
                return (
                  <option key={s.id} value={s.id} disabled={why !== null}>
                    {s.name} ({s.region}){why ? ` — ${why}` : ""}
                  </option>
                );
              })}
            </SelectField>
          ))}
        {kind === "selector" && (
          <TextField
            label={t("firewalls.selector")}
            name="selector"
            value={selector}
            onChange={(e) => setSelector(e.target.value)}
            spellCheck={false}
            placeholder="role=web"
            hint={t("loadBalancers.selectorHint")}
            error={
              tried && !selectorValid
                ? t("firewalls.selectorInvalid")
                : fieldError(add.error, "selector", t)
            }
          />
        )}
        {kind === "ip" && (
          <TextField
            label={t("loadBalancers.ip")}
            name="ip"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            spellCheck={false}
            placeholder="203.0.113.10"
            hint={t("loadBalancers.ipHint")}
            error={tried && !ip.trim() ? t("errors.fieldInvalid") : fieldError(add.error, "ip", t)}
          />
        )}
        {kind !== "ip" && (
          <label className="flex min-h-6 items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-4 accent-accent"
              disabled={!shared}
              checked={usePrivateIp && shared}
              onChange={(e) => setUsePrivateIp(e.target.checked)}
            />
            <span className="flex flex-col">
              <span className="font-medium">{t("loadBalancers.usePrivateIp")}</span>
              <span className="text-xs text-muted">
                {shared
                  ? t("loadBalancers.usePrivateIpHint")
                  : t("loadBalancers.usePrivateIpNeedsNetwork")}
              </span>
            </span>
          </label>
        )}
        <FieldProblems error={add.error} />
        <ErrorMessage error={add.error} />
      </form>
    </Dialog>
  );
}

const targetKey = (tg: LoadBalancerTarget) =>
  tg.type === "server"
    ? `server:${tg.serverRef}`
    : tg.type === "ip"
      ? `ip:${tg.ip}`
      : `selector:${tg.selector}`;

function TargetsTab({ projectId, lb, readOnly, busy }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const remove = useRemoveTarget(projectId, lb.id);
  const [dialog, setDialog] = useState<TargetDialog>(null);
  const [removing, setRemoving] = useState<LoadBalancerTarget | null>(null);
  const disabled = readOnly || busy;
  const nameOf = (tg: LoadBalancerTarget) =>
    tg.type === "server" ? tg.server.name : tg.type === "ip" ? tg.ip : tg.selector;
  const buttons = (
    <div className="flex flex-wrap gap-2">
      <Button
        onClick={() => setDialog("server")}
        disabled={disabled}
        icon={<Server className="size-4" aria-hidden />}
      >
        {t("loadBalancers.addServer")}
      </Button>
      <Button
        variant="secondary"
        onClick={() => setDialog("selector")}
        disabled={disabled}
        icon={<Tags className="size-4" aria-hidden />}
      >
        {t("loadBalancers.addSelector")}
      </Button>
      <Button
        variant="secondary"
        onClick={() => setDialog("ip")}
        disabled={disabled}
        icon={<Globe className="size-4" aria-hidden />}
      >
        {t("loadBalancers.addIp")}
      </Button>
    </div>
  );
  return (
    <>
      <Section
        title={t("loadBalancers.targets")}
        description={t("loadBalancers.targetsLead", { max: lb.limits.maxTargets })}
        actions={lb.targets.length ? buttons : null}
      >
        <DataTable<LoadBalancerTarget>
          label={t("loadBalancers.targets")}
          rows={lb.targets}
          rowKey={targetKey}
          empty={
            <EmptyState
              icon={<Server className="size-6" aria-hidden />}
              title={t("loadBalancers.noTargets")}
              action={buttons}
            >
              {t("loadBalancers.noTargetsLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("loadBalancers.target"),
              primary: true,
              cell: (tg) =>
                tg.type === "server" && tg.server.id ? (
                  <Link
                    to="/projects/$projectId/servers/$serverId"
                    params={{ projectId, serverId: tg.server.id }}
                    className="font-medium text-accent hover:underline"
                  >
                    {tg.server.name}
                  </Link>
                ) : (
                  <span className={tg.type === "server" ? "font-medium" : "font-mono text-xs"}>
                    {nameOf(tg)}
                  </span>
                ),
            },
            {
              key: "kind",
              header: t("loadBalancers.targetKind"),
              cell: (tg) => <Badge>{t(`loadBalancers.targetKinds.${tg.type}`)}</Badge>,
            },
            {
              key: "via",
              header: t("loadBalancers.via"),
              cell: (tg) => (
                <span className="whitespace-nowrap">
                  {tg.type === "ip"
                    ? t("loadBalancers.viaIp")
                    : tg.usePrivateIp
                      ? t("loadBalancers.viaPrivate")
                      : t("loadBalancers.viaPublic")}
                </span>
              ),
            },
            {
              key: "health",
              header: t("loadBalancers.healthTitle"),
              cell: (tg) =>
                tg.type === "label_selector" ? (
                  tg.servers.length === 0 ? (
                    <span className="text-muted">{t("firewalls.matchesNone")}</span>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {tg.servers.map((s) => (
                        <li key={s.serverRef} className="flex flex-wrap items-center gap-1 text-xs">
                          <span className="font-medium">{s.name}</span>
                          <HealthPerPort health={s.health} />
                        </li>
                      ))}
                    </ul>
                  )
                ) : (
                  <HealthPerPort health={tg.health} />
                ),
            },
          ]}
          rowMenu={(tg) => [
            [
              {
                label: t("loadBalancers.removeTarget"),
                icon: <Unplug className="size-4" aria-hidden />,
                danger: true,
                disabled,
                reason: readOnly ? t("errors.reasons.read_only_token") : t("servers.busy"),
                onSelect: () => setRemoving(tg),
              },
            ],
          ]}
        />
      </Section>
      {dialog && (
        <AddTargetDialog
          projectId={projectId}
          lb={lb}
          kind={dialog}
          onClose={() => setDialog(null)}
        />
      )}
      <DangerDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={t("loadBalancers.removeTargetTitle", { name: removing ? nameOf(removing) : "" })}
        consequences={[t("loadBalancers.removeTargetConsequence")]}
        actionLabel={t("loadBalancers.removeTarget")}
        busy={remove.isPending}
        onConfirm={() => {
          if (!removing) return;
          const body =
            removing.type === "server"
              ? removing.server.id
                ? { type: "server" as const, serverId: removing.server.id }
                : null
              : removing.type === "ip"
                ? { type: "ip" as const, ip: removing.ip }
                : { type: "label_selector" as const, selector: removing.selector };
          if (!body) return;
          remove.mutate(body, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("loadBalancers.targetRemoving") });
              setRemoving(null);
            },
            onError: (error) => toast(failureToast(t, error)),
          });
        }}
      />
    </>
  );
}

function AttachNetworkDialog({
  projectId,
  lb,
  onClose,
}: {
  projectId: string;
  lb: LoadBalancer;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const networks = useNetworks(projectId);
  const attach = useAttachLoadBalancerNetwork(projectId, lb.id);
  const [networkId, setNetworkId] = useState("");
  const [ip, setIp] = useState("");
  const joined = new Set(lb.networks.map((n) => n.id));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!networkId) return;
    attach.mutate(
      { networkId, ...(ip.trim() ? { ip: ip.trim() } : {}) },
      {
        onSuccess: () => {
          toast({ tone: "ok", title: t("loadBalancers.attaching") });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("loadBalancers.attachTitle")}
      description={t("loadBalancers.attachLead", { zone: lb.zone })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="lb-attach" busy={attach.isPending} disabled={!networkId}>
            {t("loadBalancers.attach")}
          </Button>
        </>
      }
    >
      <form id="lb-attach" onSubmit={submit} noValidate className="flex flex-col gap-4">
        <SelectField
          label={t("loadBalancers.network")}
          name="network"
          value={networkId}
          onChange={setNetworkId}
        >
          <option value="">{t("loadBalancers.chooseNetwork")}</option>
          {(networks.data ?? []).map((n) => {
            const why = joined.has(n.id)
              ? t("loadBalancers.why.joined")
              : !n.subnets.some((s) => s.type === "cloud" && s.zone === lb.zone)
                ? t("loadBalancers.why.noSubnet")
                : null;
            return (
              <option key={n.id} value={n.id} disabled={why !== null}>
                {n.name} ({n.ipRange}){why ? ` — ${why}` : ""}
              </option>
            );
          })}
        </SelectField>
        <TextField
          label={t("loadBalancers.fixedIp")}
          name="ip"
          value={ip}
          onChange={(e) => setIp(e.target.value)}
          spellCheck={false}
          placeholder="10.0.0.10"
          hint={t("loadBalancers.fixedIpHint")}
          error={fieldError(attach.error, "ip", t)}
        />
        <FieldProblems error={attach.error} />
        <ErrorMessage error={attach.error} />
      </form>
    </Dialog>
  );
}

function NetworkingTab({ projectId, lb, readOnly, busy }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const detach = useDetachLoadBalancerNetwork(projectId, lb.id);
  const toggle = usePublicInterface(projectId, lb.id);
  const [attaching, setAttaching] = useState(false);
  const [detaching, setDetaching] = useState<LoadBalancer["networks"][number] | null>(null);
  const [switching, setSwitching] = useState(false);
  const disabled = readOnly || busy;
  return (
    <div className="flex flex-col gap-6">
      <Card>
        <Section
          title={t("loadBalancers.publicInterface")}
          description={t("loadBalancers.publicInterfaceHint")}
        >
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {lb.publicInterface ? (
              <Badge tone="ok">{t("loadBalancers.on")}</Badge>
            ) : (
              <Badge>{t("loadBalancers.off")}</Badge>
            )}
            <Button
              variant="secondary"
              disabled={disabled || (lb.publicInterface && lb.networks.length === 0)}
              onClick={() => setSwitching(true)}
            >
              {lb.publicInterface
                ? t("loadBalancers.disablePublic")
                : t("loadBalancers.enablePublic")}
            </Button>
          </div>
          {lb.publicInterface && lb.networks.length === 0 && (
            <p className="text-xs text-muted">{t("errors.reasons.lb_needs_network")}</p>
          )}
        </Section>
      </Card>
      <Section
        title={t("loadBalancers.networks")}
        description={t("loadBalancers.networksLead")}
        actions={
          <Button
            onClick={() => setAttaching(true)}
            disabled={disabled}
            icon={<Plus className="size-4" aria-hidden />}
          >
            {t("loadBalancers.attach")}
          </Button>
        }
      >
        <DataTable<LoadBalancer["networks"][number]>
          label={t("loadBalancers.networks")}
          rows={lb.networks}
          rowKey={(n) => n.networkRef}
          empty={<Alert tone="neutral">{t("loadBalancers.noNetworkLead")}</Alert>}
          columns={[
            {
              key: "name",
              header: t("loadBalancers.network"),
              primary: true,
              cell: (n) =>
                n.id ? (
                  <Link
                    to="/projects/$projectId/networks/$networkId"
                    params={{ projectId, networkId: n.id }}
                    className="font-medium text-accent hover:underline"
                  >
                    {n.name}
                  </Link>
                ) : (
                  n.name
                ),
            },
            {
              key: "ip",
              header: t("loadBalancers.privateIp"),
              cell: (n) => <span className="font-mono">{n.ip}</span>,
            },
          ]}
          rowMenu={(n) => [
            [
              {
                label: t("loadBalancers.detach"),
                icon: <Unplug className="size-4" aria-hidden />,
                danger: true,
                disabled: disabled || n.id === null,
                reason: readOnly ? t("errors.reasons.read_only_token") : t("servers.busy"),
                onSelect: () => setDetaching(n),
              },
            ],
          ]}
        />
      </Section>
      {attaching && (
        <AttachNetworkDialog projectId={projectId} lb={lb} onClose={() => setAttaching(false)} />
      )}
      <DangerDialog
        open={detaching !== null}
        onOpenChange={(open) => !open && setDetaching(null)}
        title={t("loadBalancers.detachTitle", { name: detaching?.name ?? "" })}
        consequences={[t("loadBalancers.detachConsequence")]}
        actionLabel={t("loadBalancers.detach")}
        busy={detach.isPending}
        onConfirm={() =>
          detaching?.id &&
          detach.mutate(
            { networkId: detaching.id },
            {
              onSuccess: () => {
                toast({ tone: "ok", title: t("loadBalancers.detaching") });
                setDetaching(null);
              },
              onError: (error) => toast(failureToast(t, error)),
            },
          )
        }
      />
      <DangerDialog
        open={switching}
        onOpenChange={setSwitching}
        title={
          lb.publicInterface
            ? t("loadBalancers.disablePublicTitle")
            : t("loadBalancers.enablePublicTitle")
        }
        consequences={[
          lb.publicInterface
            ? t("loadBalancers.disablePublicConsequence")
            : t("loadBalancers.enablePublicConsequence"),
        ]}
        actionLabel={
          lb.publicInterface ? t("loadBalancers.disablePublic") : t("loadBalancers.enablePublic")
        }
        busy={toggle.isPending}
        onConfirm={() =>
          toggle.mutate(
            { enabled: !lb.publicInterface },
            {
              onSuccess: () => {
                toast({ tone: "ok", title: t("loadBalancers.publicSwitching") });
                setSwitching(false);
              },
              onError: (error) => toast(failureToast(t, error)),
            },
          )
        }
      />
    </div>
  );
}

function TypeTab({ projectId, lb, readOnly, busy }: TabProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const types = useCatalogue(projectId, "load-balancer-types").data ?? [];
  const prices = useCatalogue(projectId, "prices").data;
  const change = useChangeLoadBalancerType(projectId, lb.id);
  const [chosen, setChosen] = useState(lb.type);
  const fmt = (amount: string | undefined) =>
    amount === undefined ? "—" : money(amount, prices?.currency ?? "EUR", i18n.language);
  const certs = new Set(lb.services.flatMap((s) => s.certificates.map((c) => c.id ?? c.name))).size;
  return (
    <Card>
      <Section
        title={t("loadBalancers.changeType")}
        description={t("loadBalancers.changeTypeLead")}
      >
        <fieldset className="flex flex-col gap-2 text-sm">
          <legend className="sr-only">{t("loadBalancers.type")}</legend>
          {types.map((type) => {
            const price = loadBalancerPrice({ type: type.code, region: lb.region }, types);
            const tooSmall =
              lb.services.length > type.maxServices ||
              lb.targets.length > type.maxTargets ||
              certs > type.maxCertificates;
            const why =
              type.code === lb.type
                ? t("loadBalancers.why.currentType")
                : !price
                  ? t("loadBalancers.notOffered")
                  : tooSmall
                    ? t("loadBalancers.why.tooSmall")
                    : null;
            return (
              <label
                key={type.code}
                className="flex cursor-pointer items-start gap-2 rounded-lg border border-line px-3 py-2 has-[:disabled]:opacity-60"
              >
                <input
                  type="radio"
                  name="type"
                  className="mt-0.5 size-4 accent-accent"
                  checked={chosen === type.code}
                  disabled={readOnly || (why !== null && type.code !== lb.type)}
                  onChange={() => setChosen(type.code)}
                />
                <span className="flex flex-col">
                  <span className="font-medium">
                    {type.code.toUpperCase()} — {fmt(price?.monthly.net)}
                  </span>
                  <span className="text-xs text-muted">
                    {t("loadBalancers.typeLimits", {
                      services: type.maxServices,
                      targets: type.maxTargets,
                      certificates: type.maxCertificates,
                    })}
                  </span>
                  {why && <span className="text-xs text-muted">{why}</span>}
                </span>
              </label>
            );
          })}
        </fieldset>
        <div>
          <Button
            busy={change.isPending}
            disabled={readOnly || busy || chosen === lb.type}
            onClick={() =>
              change.mutate(
                { type: chosen },
                {
                  onSuccess: () => toast({ tone: "ok", title: t("loadBalancers.typeChanging") }),
                  onError: (error) => toast(failureToast(t, error)),
                },
              )
            }
          >
            {t("loadBalancers.changeTypeSubmit")}
          </Button>
        </div>
        <p className="text-xs text-muted">{t("loadBalancers.billing")}</p>
      </Section>
    </Card>
  );
}

function SettingsTab({ projectId, lb, readOnly }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateLoadBalancer(projectId, lb.id);
  return (
    <div className="flex flex-col gap-4">
      <NameAndLabels
        name={lb.name}
        labels={lb.labels}
        readOnly={readOnly}
        saving={update.isPending}
        error={update.error}
        onSave={(change) =>
          update.mutate(change, {
            onSuccess: () => toast({ tone: "ok", title: t("loadBalancers.saved") }),
          })
        }
      />
    </div>
  );
}

function DeleteTab({ projectId, lb, readOnly, busy }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const remove = useDeleteLoadBalancer(projectId);
  const protection = useLoadBalancerProtection(projectId, lb.id);
  const [confirming, setConfirming] = useState(false);
  const locked = lb.protection.delete;
  return (
    <Card>
      <div className="flex flex-col gap-4">
        {locked ? (
          <Alert tone="warn">{t("loadBalancers.deleteProtected")}</Alert>
        ) : (
          <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
            {lb.ipv4 && <li>{t("loadBalancers.deleteAddresses", { address: lb.ipv4 })}</li>}
            <li>{t("loadBalancers.deleteServices")}</li>
            <li>{t("loadBalancers.deleteTargets")}</li>
            <li>{t("loadBalancers.deleteBilling")}</li>
          </ul>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            busy={protection.isPending}
            disabled={readOnly || busy}
            icon={
              locked ? (
                <LockOpen className="size-4" aria-hidden />
              ) : (
                <Lock className="size-4" aria-hidden />
              )
            }
            onClick={() =>
              protection.mutate(
                { delete: !locked },
                { onError: (error) => toast(failureToast(t, error)) },
              )
            }
          >
            {locked ? t("loadBalancers.unprotect") : t("loadBalancers.protect")}
          </Button>
          <Button
            variant="danger"
            disabled={readOnly || busy || locked}
            icon={<Trash2 className="size-4" aria-hidden />}
            onClick={() => setConfirming(true)}
          >
            {t("loadBalancers.delete")}
          </Button>
        </div>
      </div>
      <DeleteLoadBalancerDialog
        projectId={projectId}
        lb={confirming ? lb : null}
        busy={remove.isPending}
        onClose={() => setConfirming(false)}
        onConfirm={() =>
          // The page unmounts once the copy no longer has the balancer: no component callbacks.
          void remove.mutateAsync(lb.id).then(
            () => {
              toast({ tone: "ok", title: t("loadBalancers.deleted", { name: lb.name }) });
              void navigate({ to: "/projects/$projectId/load-balancers", params: { projectId } });
            },
            (error: unknown) => toast(failureToast(t, error)),
          )
        }
      />
    </Card>
  );
}

export function LoadBalancerPage({
  projectId,
  loadBalancerId,
  tab,
}: {
  projectId: string;
  loadBalancerId: string;
  tab: LoadBalancerTab;
}) {
  const { t } = useTranslation();
  const project = useProject(projectId);
  const lb = useLoadBalancer(projectId, loadBalancerId);
  const running = useRunningOperations(projectId);
  const readOnly = project.data?.connection?.permission === "read";
  const back = (
    <Link
      to="/projects/$projectId/load-balancers"
      params={{ projectId }}
      className="inline-flex min-h-6 items-center gap-1 text-sm text-muted hover:text-text"
    >
      <ArrowLeft className="size-4" aria-hidden />
      {t("loadBalancers.back")}
    </Link>
  );
  if (lb.error) {
    const gone = lb.error instanceof ApiProblem && lb.error.status === 404;
    return (
      <>
        <PageHeader
          title={t("nav.loadBalancers")}
          intro={gone ? t("loadBalancers.notFound") : undefined}
        />
        {back}
        {!gone && <ErrorMessage error={lb.error} />}
      </>
    );
  }
  if (!lb.data) return <Spinner label={t("common.loading")} />;
  const data = lb.data;
  const progress = progressOf(running.data, data.id);
  const props: TabProps = { projectId, lb: data, readOnly, busy: progress !== null };
  const views: Record<LoadBalancerTab, ReactNode> = {
    overview: <OverviewTab key={data.algorithm} {...props} />,
    services: <ServicesTab {...props} />,
    targets: <TargetsTab {...props} />,
    networking: <NetworkingTab {...props} />,
    type: <TypeTab key={data.type} {...props} />,
    settings: <SettingsTab key={`${data.name}|${formatLabels(data.labels)}`} {...props} />,
    delete: <DeleteTab {...props} />,
  };
  return (
    <>
      {back}
      <PageHeader
        title={data.name}
        help="load-balancers"
        meta={
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge>{data.type.toUpperCase()}</Badge>
            <Badge>{data.region}</Badge>
            <HealthBadge health={healthSummary(data)} />
            {data.protection.delete && <Badge tone="ok">{t("loadBalancers.protected")}</Badge>}
            {data.ipv4 && data.publicInterface && (
              <span className="font-mono text-xs text-muted">{data.ipv4}</span>
            )}
          </div>
        }
        intro={t(`loadBalancers.tabIntro.${tab}`)}
      />
      {readOnly && <Alert tone="warn">{t("errors.reasons.read_only_token")}</Alert>}
      {progress !== null && (
        <Alert tone="neutral">
          {t("loadBalancers.running")} {progress}%
        </Alert>
      )}
      <Tabs
        label={data.name}
        active={tab}
        items={LOAD_BALANCER_TABS.map((key) => ({
          key,
          label: t(`loadBalancers.tabs.${key}`),
          link: {
            to: "/projects/$projectId/load-balancers/$loadBalancerId",
            params: { projectId, loadBalancerId },
            search: { tab: key },
          },
        }))}
      />
      {views[tab]}
    </>
  );
}
